import React, { useState } from 'react';
import { ChevronDownIcon, CopyIcon, GripVerticalIcon, Trash2Icon } from 'lucide-react';
import { ConditionalOperator, FormQuestion, OPERATOR_LABELS, QUESTION_TYPE_LABELS, QuestionType } from '../../types';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Textarea } from '../ui/Textarea';
import { Switch } from '../ui/Switch';
import { Button } from '../ui/Button';
interface QuestionCardProps {
  question: FormQuestion;
  index: number;
  previousQuestions: FormQuestion[];
  onChange: (question: FormQuestion) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onDragStart: () => void;
  onDragOver: (event: React.DragEvent) => void;
  onDrop: () => void;
  dragging: boolean;
}
const TYPE_OPTIONS = (Object.keys(QUESTION_TYPE_LABELS) as QuestionType[]).map(type => ({
  value: type,
  label: QUESTION_TYPE_LABELS[type]
}));
const OPERATOR_OPTIONS = (Object.keys(OPERATOR_LABELS) as ConditionalOperator[]).map(op => ({
  value: op,
  label: OPERATOR_LABELS[op]
}));
const NEEDS_OPTIONS: QuestionType[] = ['seleccion_unica', 'seleccion_multiple', 'desplegable'];
export function QuestionCard({
  question,
  index,
  previousQuestions,
  onChange,
  onDuplicate,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  dragging
}: QuestionCardProps) {
  const [open, setOpen] = useState(false);
  const logic = question.conditionalLogic;
  function patch(partial: Partial<FormQuestion>) {
    onChange({
      ...question,
      ...partial
    });
  }
  return <li draggable onDragStart={onDragStart} onDragOver={onDragOver} onDrop={onDrop} className={`rounded-lg border bg-background shadow-sm transition-opacity ${dragging ? 'border-primary opacity-60' : 'border-border'}`} data-id="element-258" data-mp="2vyitz0jnm6p0">
      <div className="flex items-center gap-2 p-3" data-id="element-259" data-mp="34zgxziu3ee7l">
        <span className="cursor-grab text-muted-foreground active:cursor-grabbing" aria-hidden="true" data-id="element-260" data-mp="f9dbvpzm3mhx">
          <GripVerticalIcon className="h-4 w-4" data-id="element-261" data-mp="k75d5am5ckys" />
        </span>
        <span className="flex h-6 w-6 flex-none items-center justify-center rounded-md bg-surface text-xs font-semibold text-muted-foreground" data-id="element-262" data-mp="k9rlvdkrx6il">
          {index + 1}
        </span>
        <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="min-w-0 flex-1 text-left" data-id="element-263" data-mp="2c983s4zoyh4q">
          <span className="block truncate text-sm font-medium text-foreground" data-id="element-264" data-mp="2la68kphyesod">
            {question.label}
          </span>
          <span className="block text-xs text-muted-foreground" data-id="element-265" data-mp="1xk21u99gyct5">
            {QUESTION_TYPE_LABELS[question.type]}
            {question.required ? ' · Obligatoria' : ''}
            {logic.enabled ? ' · Condicional' : ''}
          </span>
        </button>
        <button type="button" onClick={onDuplicate} aria-label="Duplicar pregunta" className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-266" data-mp="27cho3irstnju">
          <CopyIcon className="h-4 w-4" data-id="element-267" data-mp="1etc0wq3gl02x" />
        </button>
        <button type="button" onClick={onDelete} aria-label="Eliminar pregunta" className="rounded-md p-2 text-muted-foreground hover:bg-surface hover:text-red-600" data-id="element-268" data-mp="1err752oyxl64">
          <Trash2Icon className="h-4 w-4" data-id="element-269" data-mp="3lgt89xznkook" />
        </button>
        <button type="button" onClick={() => setOpen(v => !v)} aria-label={open ? 'Contraer' : 'Desplegar'} className="rounded-md p-2 text-muted-foreground hover:bg-surface" data-id="element-270" data-mp="3q9mcmkr7lv04">
          <ChevronDownIcon className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} data-id="element-271" data-mp="3gj9hxzpoklg9" />
        </button>
      </div>

      {open && <div className="space-y-4 border-t border-border p-4" data-id="element-272" data-mp="84j2glmdyssu">
          <Input id={`${question.id}-label`} label="Pregunta" value={question.label} onChange={e => patch({
        label: e.target.value
      })} data-id="element-273" data-mp="d2kisysfge0m" />
          <Input id={`${question.id}-desc`} label="Descripción (opcional)" value={question.description ?? ''} onChange={e => patch({
        description: e.target.value
      })} data-id="element-274" data-mp="3v7e2zac7h927" />
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-275" data-mp="2jsr01cxy46v9">
            <Select id={`${question.id}-type`} label="Tipo de pregunta" options={TYPE_OPTIONS} value={question.type} onChange={e => patch({
          type: e.target.value as QuestionType
        })} data-id="element-276" data-mp="2jvd8desnuqej" />
            <Input id={`${question.id}-placeholder`} label="Placeholder" value={question.placeholder ?? ''} onChange={e => patch({
          placeholder: e.target.value
        })} data-id="element-277" data-mp="343l9xfcssivu" />
          </div>

          {NEEDS_OPTIONS.includes(question.type) && <Textarea id={`${question.id}-options`} label="Opciones (una por línea)" rows={5} value={question.options.join('\n')} onChange={e => patch({
        options: e.target.value.split('\n').map(o => o.trimStart()).filter((o, i, arr) => o !== '' || i === arr.length - 1)
      })} data-id="element-278" data-mp="2qb5303gs00pn" />}

          <div className="flex flex-wrap gap-6" data-id="element-279" data-mp="3dzepz8pq6uid">
            <Switch id={`${question.id}-required`} label="Obligatoria" checked={question.required} onChange={checked => patch({
          required: checked
        })} data-id="element-280" data-mp="3dy3gqtdigujv" />
            <Switch id={`${question.id}-active`} label="Activa" checked={question.isActive} onChange={checked => patch({
          isActive: checked
        })} data-id="element-281" data-mp="22lj1vagufbf0" />
          </div>

          {/* Lógica condicional */}
          <div className="rounded-lg border border-border bg-surface p-4" data-id="element-282" data-mp="d572d4pb405j">
            <Switch id={`${question.id}-logic`} label="Mostrar esta pregunta solo si se cumple una condición" checked={logic.enabled} onChange={checked => patch({
          conditionalLogic: {
            ...logic,
            enabled: checked
          }
        })} data-id="element-283" data-mp="i3s10zowubrv" />
            {logic.enabled && <div className="mt-4 grid gap-3 sm:grid-cols-3" data-id="element-284" data-mp="vuwutl5lk9ua">
                <Select id={`${question.id}-logic-q`} label="Pregunta anterior" placeholder="Selecciona" options={previousQuestions.map(q => ({
            value: q.id,
            label: q.label
          }))} value={logic.questionId ?? ''} onChange={e => patch({
            conditionalLogic: {
              ...logic,
              questionId: e.target.value
            }
          })} data-id="element-285" data-mp="vwhshjn7k8wd" />
                <Select id={`${question.id}-logic-op`} label="Operador" options={OPERATOR_OPTIONS} value={logic.operator} onChange={e => patch({
            conditionalLogic: {
              ...logic,
              operator: e.target.value as ConditionalOperator
            }
          })} data-id="element-286" data-mp="37m0t28ke2qou" />
                <Input id={`${question.id}-logic-value`} label="Respuesta" value={logic.value} disabled={logic.operator === 'respondida' || logic.operator === 'no_respondida'} onChange={e => patch({
            conditionalLogic: {
              ...logic,
              value: e.target.value
            }
          })} data-id="element-287" data-mp="dtstc65qfizr" />
              </div>}
          </div>

          <div className="flex justify-end" data-id="element-288" data-mp="wpivbtdo0fdn">
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} data-id="element-289" data-mp="wog23esve0uk">
              Cerrar
            </Button>
          </div>
        </div>}
    </li>;
}